import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HeartIcon, ClockIcon, ShieldCheckIcon, SunIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react';
const CAREGIVER_IMG = 'https://images.unsplash.com/photo-1581578731548-c64695cc6958?q=80&w=2000&auto=format&fit=crop';
const SENIORS_IMG = 'https://cdn.magicpatterns.com/uploads/d6jS4Kkp3VskdBgjLTMsA2/3.jpg';
export function RespiteCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-1230">
      <PageBanner title="Respite & Holiday Care" subtitle="Take the break you deserve while your loved one receives compassionate, professional care." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Respite & Holiday Care',
      path: '/services/respite-care'
    }]} data-id="element-1231" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-light-alt to-white" data-id="element-1232">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1233">
          <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1234">
            <div data-id="element-1235">
              <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-1236">
                <HeartIcon className="h-4 w-4 text-primary" data-id="element-1237" />
                <span className="text-primary text-sm font-semibold" data-id="element-1238">
                  Caregiver Support
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-1239">
                You Deserve a Break — We're Here to Help
              </h2>
              <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-1240">
                <p data-id="element-1241">
                  As a family member in the role of caregiver, you may
                  experience extreme stress. The demands of your own family,
                  coupled with the needs of a parent or loved one can overwhelm.{' '}
                  <strong className="text-dark" data-id="element-1242">
                    Take time off and refresh yourself!
                  </strong>
                </p>
                <p data-id="element-1243">
                  Our Holiday and Respite Care service will enable you to take a
                  vacation, go away for the holidays, or even just take an
                  afternoon per week off. Edvin Home Healthcare Solutions
                  certified nursing assistants or companions can care for your
                  loved one to the extent needed, allowing you to take the "time
                  out" you need.
                </p>
              </div>

              {/* RN Badge */}
              <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-1244">
                <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-1245" />
                <div data-id="element-1246">
                  <h4 className="font-bold text-dark" data-id="element-1247">RN Supervised — 24/7</h4>
                  <p className="text-sm text-dark-muted" data-id="element-1248">
                    All care providers are supervised by a RN Case Manager, who
                    is on-call 24 hours a day, 7 days a week.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-card-hover" data-id="element-1249">
              <img src={CAREGIVER_IMG} alt="Caregiver providing compassionate care to elderly couple" className="w-full h-80 lg:h-[420px] object-cover" data-id="element-1250" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-light" data-id="element-1251">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1252">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1253">
            <div className="text-center mb-14" data-id="element-1254">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1255">
                A Day Off With No Worries
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1256"></div>
              <p className="text-lg text-dark-muted max-w-3xl mx-auto" data-id="element-1257">
                The caregivers of Edvin Home Healthcare Solutions are ready to
                watch over your loved one in the same considerate way you do,
                giving you a chance to get away.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1258">
              {[{
              icon: SunIcon,
              title: 'Vacation & Travel',
              desc: 'Go on vacation or travel for the holidays knowing your loved one is in safe, caring hands.'
            }, {
              icon: CalendarIcon,
              title: 'Flexible Scheduling',
              desc: 'We provide respite care daily, weekly, or as needed — from a few hours to 24-hour care.'
            }, {
              icon: HeartIcon,
              title: 'Peace of Mind',
              desc: "Count on us for the break you need and complete peace of mind while you're away."
            }, {
              icon: ClockIcon,
              title: 'Any Duration',
              desc: 'Few hours, overnight, 1 hour to 24 hours — we arrange care the way you need it.'
            }].map((benefit, idx) => <div key={idx} className="bg-light rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-1259">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-1260">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-1261" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-1262">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-1263">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-gradient-to-t from-light-alt to-white" data-id="element-1264">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1265">
          <div ref={detailsRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1266">
            <div className="order-2 lg:order-1 rounded-2xl overflow-hidden shadow-card-hover" data-id="element-1267">
              <img src={SENIORS_IMG} alt="Happy seniors enjoying life together" className="w-full h-80 lg:h-[400px] object-cover" data-id="element-1268" />
            </div>

            <div className="order-1 lg:order-2" data-id="element-1269">
              <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-1270">
                Personalized to Meet Your Needs
              </h2>
              <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-1271">
                Our Respite Care program is a solution when you go on vacation,
                travel for holidays, attend parties, or just need to run a few
                errands. You can count on us for the break you need and peace of
                mind while you're away.
              </p>

              <h3 className="text-xl font-bold text-dark mb-4" data-id="element-1272">
                What is Respite Care?
              </h3>
              <p className="text-dark-muted mb-6 leading-relaxed" data-id="element-1273">
                Respite care provides short-term relief for primary caregivers.
                It can be arranged for just an afternoon or for several days or
                weeks. Care can be provided at home, in a healthcare facility,
                or at an adult day center.
              </p>
              <p className="text-dark-muted mb-6 leading-relaxed" data-id="element-1274">
                Whether it's for a few hours a week to run errands, or a couple
                of weeks to take a much-needed vacation, respite care offers you
                the chance to step back and recharge.
              </p>
              <p className="text-dark-muted leading-relaxed" data-id="element-1275">
                You might need a break from time to time, maybe just one
                afternoon per week off. Edvin Home Health Care Solution and
                Nursing Services can provide that much-needed relief. We can
                also provide overnight care or 24-hour care while you take a
                vacation.
              </p>

              <h3 className="text-xl font-bold text-dark mb-4" data-id="element-1276">
                Professional & Compassionate Care
              </h3>
              <p className="text-dark-muted" data-id="element-1277">
                The caregivers of Edvin Home Health Care Solution and Nursing
                Services are ready to step in with the same affection and
                concern you have for your loved one. Our professionals are
                specially trained to handle the unique needs of your loved one,
                providing consistent, high-quality care.
              </p>

              <h3 className="text-xl font-bold text-dark mb-4" data-id="element-1278">
                Time to Recharge
              </h3>
              <p className="text-dark-muted" data-id="element-1279">
                Edvin Home Health Care Solution and Nursing Services gives you
                the time to rediscover yourself. Take a walk, read a book, or
                simply rest. When you take care of yourself, you're better
                equipped to take care of your loved one and handle the
                challenges of caregiving with greater resilience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-1280">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1281">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1282">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1283"></div>
            <div className="relative z-10" data-id="element-1284">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1285">
                Ready for a Well-Deserved Break?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1286">
                Contact us today to discuss your respite care needs. We'll
                create a personalized plan so you can take time off with
                complete peace of mind.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1287">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1288" />
    </main>;
}